import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Uploader } from "../components/Uploader";
import { ResultCells } from "../components/ResultView";
import { MarkdownCell, CodeCell, Stream } from "../components/Cell";
import { identifyFile, identifyUrl, getId, errMsg } from "../lib/api";

const SETUP = `import requests
from moth_ai import EnsembleIdentifier, uncertainty

GBIF  = "https://api.gbif.org/v1"
INAT  = "https://api.inaturalist.org/v1"
model = EnsembleIdentifier(backbone="gemini-3-flash", passes=3)
TAU_CONF, TAU_ENTROPY = 0.60, 0.50   # abstention thresholds`;

export default function IdentifyPage({ openId, setKernel, onExplore }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (openId) getId(openId).then((r) => { setResult(r); setSrc(r.filename); }).catch((e) => toast.error(errMsg(e)));
  }, [openId]);

  const run = async (fn, name) => {
    setLoading(true); setResult(null); setError(null); setSrc(name); setKernel("busy");
    try {
      const r = await fn();
      setResult(r);
      toast.success(r.uncertain ? "Done — flagged for expert review" : "Done — high confidence");
    } catch (e) { setError(errMsg(e)); toast.error(errMsg(e)); } finally { setLoading(false); setKernel("idle"); }
  };

  return (
    <div>
      <MarkdownCell testid="intro-markdown-cell">
        <h1>Moth AI: Uncertainty-Aware Moth Identification</h1>
        <p>Development & evaluation of an <b>uncertainty-aware</b> moth identification system with taxonomic integration via the <code>GBIF Species API</code> and the <code>iNaturalist API</code>.</p>
        <p>Pipeline: a 3-pass vision ensemble ranks the top-5 species → predictive entropy & ensemble agreement quantify uncertainty → every candidate name is reconciled against the GBIF backbone → research-grade iNaturalist observations provide visual evidence.</p>
      </MarkdownCell>
      <CodeCell n={1} code={SETUP} testid="setup-cell" />
      <MarkdownCell><h2>1. Load a specimen image</h2><p>Upload a photo (optionally with its known species for evaluation), or load one of the iNaturalist research-grade reference specimens.</p></MarkdownCell>
      <CodeCell n={2} code={`img = load_image(${src ? `"${src}"` : "..."})\nresult = model.identify(img, top_k=5)`} running={loading} testid="identify-cell">
        <Uploader loading={loading}
          onFile={(f, label, rr) => run(() => identifyFile(f, label, rr), f.name)}
          onUrl={(u, label, rr) => run(() => identifyUrl(u, label, rr), u.split("/").slice(-2).join("/"))} />
        {loading && <Stream testid="identify-running-stream">{"[ensemble] running 3 independent passes…\n[rerank] scoring specimen against iNat reference photos…\n[gbif] reconciling names with backbone…\n[inat] fetching research-grade observations…  (~20–50 s)"}</Stream>}
        {error && <Stream kind="stderr" testid="identify-error-stream">{`---------------------------------------------------------------------------\nIdentificationError: ${error}`}</Stream>}
      </CodeCell>
      {result && <ResultCells r={result} onExplore={onExplore} />}
    </div>
  );
}
